/**
 * PauzeRegelsModal — Referentie-overzicht voor automatische pauze-berekening.
 * Bron: api/_lib/break-policy.ts (SSOT).
 */

import { useEffect } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function PauzeRegelsModal({ open, onClose }: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-heading font-semibold text-main">Pauze-regels</h2>
            <p className="text-xs text-muted">Zo wordt pauze automatisch berekend bij klok-uit</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="Sluiten">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <section className="mb-5">
          <h3 className="text-sm font-semibold text-main mb-2">Wasserij</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted border-b">
                <th className="py-1.5 font-medium">Gewerkt</th>
                <th className="py-1.5 font-medium text-right">Pauze</th>
                <th className="py-1.5 font-medium text-right">Opbouw</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <tr><td className="py-2">7u of meer</td><td className="text-right font-mono font-medium">60 min</td><td className="text-right text-xs text-muted">2× koffie + lunch</td></tr>
              <tr><td className="py-2">5,5 – 7u</td><td className="text-right font-mono font-medium">45 min</td><td className="text-right text-xs text-muted">1× koffie + lunch</td></tr>
              <tr><td className="py-2">2,5 – 5,5u</td><td className="text-right font-mono font-medium">15 min</td><td className="text-right text-xs text-muted">1× koffie</td></tr>
              <tr><td className="py-2">Korter dan 2,5u</td><td className="text-right font-mono font-medium">0 min</td><td className="text-right text-xs text-muted">Invalshift</td></tr>
            </tbody>
          </table>
        </section>

        <section className="mb-5">
          <h3 className="text-sm font-semibold text-main mb-2">Chauffeur (route)</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted border-b">
                <th className="py-1.5 font-medium">Gewerkt</th>
                <th className="py-1.5 font-medium text-right">Pauze totaal</th>
                <th className="py-1.5 font-medium text-right">Waarvan betaald</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <tr><td className="py-2">Meer dan 10u</td><td className="text-right font-mono font-medium">45 min</td><td className="text-right font-mono text-emerald-600">15 min</td></tr>
              <tr><td className="py-2">5,5 – 10u</td><td className="text-right font-mono font-medium">30 min</td><td className="text-right text-slate-300">—</td></tr>
              <tr><td className="py-2">5,5u of korter</td><td className="text-right font-mono font-medium">0 min</td><td className="text-right text-slate-300">—</td></tr>
            </tbody>
          </table>
          <p className="mt-2 text-xs text-muted">
            Bij &gt;10u dienst komt er volgens de Arbeidstijdenwet 15 min doorbetaalde pauze bij.
          </p>
        </section>

        <section className="mb-5">
          <h3 className="text-sm font-semibold text-main mb-2">Kantoor</h3>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-slate-100">
              <tr><td className="py-2">Meer dan 5,5u</td><td className="text-right font-mono font-medium">30 min</td></tr>
              <tr><td className="py-2">5,5u of korter</td><td className="text-right font-mono font-medium">15 min</td></tr>
            </tbody>
          </table>
          <p className="mt-2 text-xs text-muted">Korte (ochtend)shift, bv. 08:45-13:00: 15 min koffiepauze.</p>
        </section>

        <section className="mb-5">
          <h3 className="text-sm font-semibold text-main mb-2">Algemeen</h3>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-slate-100">
              <tr><td className="py-2">Meer dan 5,5u</td><td className="text-right font-mono font-medium">30 min</td></tr>
              <tr><td className="py-2">5,5u of korter</td><td className="text-right font-mono font-medium">0 min</td></tr>
            </tbody>
          </table>
          <p className="mt-2 text-xs text-muted">Wettelijk minimum. Geldt als er geen shift in het rooster staat en het team geen eigen regels heeft.</p>
        </section>

        <section className="rounded-lg bg-slate-50 p-3 text-xs text-muted space-y-1.5">
          <p className="font-semibold text-main">Hoe werkt het in de praktijk?</p>
          <p>Bij het uitklokken kijkt het systeem eerst naar het <strong>rooster</strong> voor de shift (Wasserij / Chauffeur / Kantoor). Staat daar niks, dan valt 'ie terug op het <strong>team</strong> van de medewerker. Anders: algemeen.</p>
          <p>Heeft een medewerker in het rooster <strong>Wasserij</strong> staan, dan telt hij volgens de wasserij-regels. Chauffeur-shift? Dan de route-regels.</p>
          <p><strong>Verwacht en werkelijk delen dezelfde regels</strong>: het verwachte urensaldo uit het werkrooster gebruikt exact deze pauzestaffels, dus verwacht en geklokt kunnen niet uiteenlopen door pauzes.</p>
          <p>Een admin kan in de uren-pagina altijd handmatig de pauze aanpassen. Die keuze wordt gerespecteerd en niet overschreven.</p>
        </section>
      </div>
    </div>
  );
}
